<template>
  <div>

    <h4 class="mb-3 mt-2 d-flex align-items-center" v-if="block.title && showTitle">
      <i v-if="block.icon" :class="[block.icon, 'mr-1', 'fa-lg']"></i>
      {{block.title}}
      <custom-tooltip class="ml-2" v-if="block.tooltipName && block.tooltipText" :name="block.tooltipName">
        {{ $t(block.tooltipText) }}
      </custom-tooltip>
      <slot name="title-suffix" v-bind:params="params"></slot>
    </h4>

    <div v-if="block.description" class="text-muted mb-3">{{block.description}}</div>

    <b-row class="align-items-end">

      <b-col
        :md="block.columns"
        v-for="(input, index) in selects"
        :key="`${index}${input.name}`"
      >
        <label :for="input.name">{{ input.label}}</label>
        <multiselect
          :loading="isSelectLoading[input.name]"
          class="mb-4"
          :disabled="input.disabled"
          v-model="params[input.name]"
          :multiple="input.multiple"
          :id="input.name"
          :label="input.labelBy || 'name'"
          :track-by="input.trackBy || 'name'"
          :placeholder="input.placeholder"
          :options="selectOptions[input.name]"
          :searchable="input.searchable"
          :allow-empty="input.allow_empty ? input.allow_empty : false"
          :taggable="input.taggable"
          :close-on-select="!input.multiple"
          :internal-search="input.internalSearch !== undefined ? input.internalSearch : true"
          @tag="value => {addTag(input.name, value)}"
          @remove="value => {createParamsObject(input.name, value)}"
          @select="value => {createParamsObject(input.name, value.value)}"
          @search-change="query => {debounceGetSelectOptions(input.options_url, input.name, input.filter_field, query)}"
          select-label=""
          selected-label=""
          :deselect-label="input.clearable == undefined || input.clearable ? 'X ' : ''"
          tag-placeholder="+">
          <template slot="noResult">
            {{$t('global.no_results')}}
          </template>
        </multiselect>
      </b-col>

      <b-col class="mb-4"
             :md="block.columns"
             v-for="(input, index) in inputsText"
             :key="`${index}${input.name}`">
        <b-input-group class=" flex-column">

          <label
            class="d-block"
            v-if="input.label"
            :for="input.name"
          >{{ input.label }}
            <custom-tooltip class="ml-2" v-if="input.tooltipName && input.tooltipText" :name="input.tooltipName">
            {{ $t(input.tooltipText) }}
          </custom-tooltip>
          </label>
          <b-form-input
            class="rounded"
            :required="input.required"
            :type="input.type"
            v-model="params[input.name]"
            :placeholder="input.placeholder"
            :disabled="isDisabled(input)"
            :name="input.name"
            :id="input.name"
            :step="input.type === 'number' ? input.step : ''"
            :min="input.min"
            :max="input.max"
            :formatter="input.formatter"
            :state="state(input)"
            @change="value => {input.type === 'number' ? createParamsObject(input.name, toNumber(value)) : createParamsObject(input.name, value)}"
          />
          <div class="invalid-feedback position-absolute mt-0 text-left" style="bottom: -16px">
            {{$t('errors.error_validation')}}
          </div>

        </b-input-group>
      </b-col>

      <b-col
        :md="block.columns"
        v-for="(input, index) in groupSelects"
        v-if="params[input.name] !== undefined"
        :key="`${index}${input.name}`"
      >
        <group-select
          v-if="plans.length > 0"
          class="mb-4"
          :input="input"
          :model="params[input.name]"
          :props_options="plans"
          @select="value => {createParamsObject(input.name, value.value)}"
        />
      </b-col>

      <b-col
        v-if="params[input.name] !== undefined"
        :md="input.md || checkboxWith"
        class="mb-4"
        v-for="(input, index) in checkboxes"
        :key="`${index}${input.name}`"
      >
        <b-form-checkbox
          :class="input.className"
          v-model="params[input.name]"
          @change="value => {createParamsObject(input.name, value)}"
        > {{input.label}}
        </b-form-checkbox>
      </b-col>

      <b-col
        v-if="params[input.name] !== undefined"
        :md="input.md || checkboxWith"
        v-for="(input, index) in switches"
        :key="`${index}${input.name}`">

        <label
          class="d-block"
          v-if="input.label"
          :for="input.name"
        >{{ input.label }}</label>

        <div class="d-flex flex-row mb-4 input-height align-items-center">
            <span class="pr-2"
                  :class="[input.title && params[input.name] ? 'disabled' : '']">{{input.titleFalse}}</span>
          <label class="switch switch-3d switch-success mb-0">
            <b-form-checkbox type="checkbox"
                             class="switch-input"
                             v-model="params[input.name]"
                             @input="value => {createParamsObject(input.name, value)}"
            />
            <input type="checkbox"
                   class="switch-input"
                   :checked="params[input.name]"
            >
            <span class="switch-slider"></span>
          </label>
          <span class="pl-2" :class="[input.title && !params[input.name] ? 'disabled' : '']">{{input.title}}</span>
        </div>
      </b-col>

      <slot name="editButtons"></slot>

    </b-row>

  </div>
</template>

<script>
  export default {
    name: 'ResourcesView',
    middleware: 'auth',
    props: {
      block: {
        type: Object,
        require: true,
      },
      prefillParams: {
        type: Object
      },
      showTitle: {
        type: Boolean,
        default: false
      },
      showAddButton: {
        type: Boolean,
        default: false
      },
      checkboxWith: {
        type: String,
        default: ''
      },
      disableAll: {
        type: Boolean,
        default: false
      },
      disabledFields: {
        type: [Boolean, Array],
        default: false
      }
    },
    data() {
      return {
        disabled: false,
        params: {
          settings: {}
        },
        selectOptions: {},
        paramsMutated: {},
        debounceGetSelectOptions: () => {
        },
        isSelectLoading: {},
      }
    },
    mounted() {
      if (this.prefillParams) {
        this.params = this.prefillParams;
      }
      this.debounceGetSelectOptions = (url, name, filter_field, value) => {_.debounce(this.getSelectOptions(url, name, filter_field, value), 1000)};
      this.disabled = this.disableAll;
    },
    computed: {
      plans() {
        return this.$store.getters['user/plans']
      },
      checkboxes() {
        return _.filter(this.block.items, input => input.type === 'checkbox');
      },
      switches() {
        return _.filter(this.block.items, input => input.type === 'switch');
      },
      inputsText() {
        return _.filter(this.block.items, input => input.type === 'text' || input.type === 'number' || input.type === 'email' || input.type === 'url');
      },
      selects() {
        let selects = _.filter(this.block.items, input => input.type === 'select' && !input.group);

        _.forEach(selects, select => {
          if (select.name === 'compromised_period') {
            this.selectOptions[select.name] = [];
            let array = new Array(30);
            for (let i = 0; i < array.length; i++) {
              if (i === 0 || i === 20) {
                this.selectOptions[select.name].push(
                    {
                      value: `${(i + 1)} day`,
                      name: `${(i + 1)} day`
                    }
                )
              } else {
                this.selectOptions[select.name].push(
                    {
                      value: `${(i + 1)} days`,
                      name: `${(i + 1)} days`
                    }
                )
              }
            }
          } else if (select.name === 'user_id'){
            this.selectOptions[select.name] = this.formatUserSelectOptions(select.options);
          } else {
            this.selectOptions[select.name] = select.options || [];
          }
        });
        this.$forceUpdate();
        return selects;
      },
      groupSelects() {
        return _.filter(this.block.items, input => input.type === 'select' && input.group);
      },
    },
    methods: {
      state(input) {
        if (input.hasOwnProperty('state')) {
          return input.state(this.params[input.name]);
        }
        return null;
      },
      toNumber(value) {
        if (value % 1 !== 0) return parseFloat(value);

        return parseInt(value)
      },
      checkIfEmpty(field) {
        if (!field) {
          return true
        }
        if (!this.params[field]) {
          return true
        } else {
          return !this.params[field].value
        }
      },
      deleteItem() {
        this.$emit('delete')
      },
      addTag(name, value) {
        const regex = {
          name: value,
          value: value,
          code: value
        };
        if (!this.params[name]) {
          this.params[name] = []
        }
        this.params[name].push(regex);
        this.selectOptions[name].push(regex);
        this.createParamsObject(name, regex.value);
      },
      createParamsObject(name, value) {
        if (_.isObject(name)) {
          value = name.value;
          name = name.field;
        }
        if (_.isObject(value)) {
          if (value instanceof Date) {
            value = this.formatDate(value, 'L');
            this.paramsMutated[name] = value;
          }
          if (Array.isArray(value)) {
            this.paramsMutated[name] = [];
            value.map(item => {
              if (this.paramsMutated[name].indexOf(item.value) > -1) {
                _.pull(this.paramsMutated[name], item.value);
              } else {
                this.paramsMutated[name].push(item.value);
              }
            })
          }
          this.paramsMutated[name] = this.formatValue(value);
        }
        this.$emit('addItem', {name: name, value: value})
      },
      getSelectOptions(url, name, filter_field, value) {
        const params = {filter: {}};
        if (name === 'user_id' && !isNaN(parseInt(value, 10))) {
          params.filter['users.id'] = value
        } else {
          params.filter[filter_field] = value;
        }
        this.$set(this.isSelectLoading, name, true);
        this.getData(url, {params}).then(({data}) => {
          this.selectOptions[name] = [];
          if (data && data.items) {
            this.selectOptions[name] = this.formatUserSelectOptions(data.items);
          }
          this.$set(this.isSelectLoading, name, false);
        }).then(() => {
          this.$forceUpdate();
          this.$set(this.isSelectLoading, name, false);
        })
      },
      isDisabled(input) {
        if (input.disabled || this.disabled) return true;

        if (Array.isArray(this.disabledFields) && this.disabledFields.includes(input.name)) return true;

        return false;
      },
      formatUserSelectOptions(items) {
        if (!Array.isArray(items)) {
          return [];
        }

        return items.map(option => {
          if (option.key) {
            return {
              label: option.title,
              value: option.key
            }
          } else if (option.first_name !== undefined && option.id) {
            if (option.first_name.trim().length === 0 && option.last_name.trim().length === 0) {
              return {
                name: `#${option.id} ${option.email}`,
                value: option.id
              }
            }
            return {
              name: `#${option.id} ${option.first_name} ${option.last_name}`,
              value: option.id
            }
          } else if (option.id) {
            return {
              label: option.title,
              value: option.id
            }
          } else if (option.title) {
            return {
              label: option.title,
              value: option.title
            }
          }
        });
      }
    },
    watch: {
      prefillParams() {
        this.params = this.prefillParams;
      },
      disableAll(value) {
        this.disabled = value
      },
      disabledFields() {
        this.$forceUpdate();
      }
    }
  }
</script>
